import React from "react";
import ModalDialog from "../../../new_components/common/modal";

interface ConfirmationModalProps {
  show: boolean;
  title: string;
  message?: string;
  data?: { label: string; value: React.ReactNode }[];
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  isSubmitting?: boolean;
  type?: "info" | "warning" | "danger" | "success";
  id?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  title,
  message,
  data,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isSubmitting = false,
  type = "info",
  id,
}) => {
  const getButtonClass = () => {
    switch (type) {
      case "danger":
        return "bg-red-600 hover:bg-red-700";
      case "warning":
        return "bg-yellow-600 hover:bg-yellow-700";
      case "success":
        return "bg-green-600 hover:bg-green-700";
      default:
        return "bg-blue-600 hover:bg-blue-700";
    }
  };

  const titleColorClass =
    type === "danger" || type === "warning"
      ? "text-red-600"
      : type === "success"
        ? "text-green-700"
        : "text-gray-900";

  return (
    <ModalDialog show={show} title="" onClose={onCancel} id={id}>
      <div className="p-4 space-y-4">
        <h3 className={`text-base font-semibold ${titleColorClass}`}>
          {title}
        </h3>
        {message && <p className="text-sm text-gray-600">{message}</p>}

        {data && data.length > 0 && (
          <div className="bg-gray-50 rounded-lg border p-4 space-y-3">
            {data.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-center text-sm"
              >
                <span className="text-gray-500 font-medium">{item.label}</span>
                <span className="font-semibold text-gray-900 border-b border-dashed border-gray-300">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            id={id ? `${id}-cancel` : undefined}
            onClick={onCancel}
            disabled={isSubmitting}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 transition-all ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50 hover:border-gray-400"
            }`}
          >
            {cancelText}
          </button>
          <button
            id={id ? `${id}-confirm` : undefined}
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all active:scale-95
              ${getButtonClass()}
              ${isSubmitting ? "cursor-not-allowed opacity-50" : ""}
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </ModalDialog>
  );
};

export default ConfirmationModal;
